import {
  Button, Column, Divider, Drawer, Heading, List, Page, Row, Spacer,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { entityTableLabelMap } from 'common/table-viewer/utils';
import { RootState } from 'client/store/configureStore';
import {
  deleteQuery, getQueries, queryDraftAdded, queryResultReset, upsertQuery,
} from 'client/store/entities/query/query';
import { IQuery } from 'common/interfaces/query';
import { QueryForm } from './query-form/query-form';
import { defaultCondition, defaultQuery, getEntityParamLabel } from './query-utils';

const Query = ({
  queries,
  getQueries,
  deleteQuery,
  upsertQuery,
}: PropsFromRedux) => {
  const [activeQuery, setActiveQuery] = useState<IQuery>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { companyId } = useParams();

  useEffect(() => {
    getQueries();
  }, []);

  useEffect(() => {
    setActiveQuery(currentQuery => queries.find(query => query.queryid === currentQuery?.queryid) ?? queries[0]);
  }, [queries]);

  useEffect(() => {
    dispatch(queryResultReset());
  }, [activeQuery]);

  const onAddQuery = () => {
    const newQuery = { ...defaultQuery({ companyId: companyId! }), queryid: uuidv4(), conditions: [defaultCondition] } as IQuery;
    dispatch(queryDraftAdded(newQuery));
    setActiveQuery(newQuery);
  };

  const queryItems = (queries || []).map(query => {
    return {
      name: `${query.queryName}`,
      active: query.queryid === activeQuery?.queryid,
      onClick: () => setActiveQuery(query),
      actions: [{
        type: 'Menu',
        subActions: [{
          label: t(translations.delete),
          icon: 'delete',
          onClick: () => deleteQuery(query.queryid || ''),
        },
        {
          label: t(translations.query_duplicateQuery),
          icon: 'duplicate',
          onClick: () => upsertQuery({ ...query, queryid: uuidv4() }),
        }],
      }],
      // TODO clarify what we should show here
      details: `${entityTableLabelMap[query?.conditions[0]?.entityType]} ${getEntityParamLabel(query?.conditions[0]?.entityType, query?.conditions[0]?.entityPropertyName)}`,
      // ${conditionLabelMap[query.conditions]} ${query.conditionValue}`,
      metadata: query.groupBy ? `${t(translations.query_groupedBy)} ${query.groupBy}` : '',
    };
  });

  return (
    <Page padding={false} scroll={false}>
      <Row spacing={0} height="100%">
        <Drawer button={false} open background="var(--color-neutral-50)">
          <div style={{ padding: 'var(--padding)' }}>
            <Row>
              <Column>
                <Heading top marginBottom={0}>{t(translations.query)}</Heading>
              </Column>
              <Column width="var(--padding-l)">
                <Button round small colored icon="add" onClick={onAddQuery} />

              </Column>
            </Row>
            <Spacer height="var(--padding-xs)" />
          </div>
          <Divider margin={0} />
          <List
            noHeader
            list={{
              items: queryItems,
            }}
          />
        </Drawer>
        <Column scroll borderLeft>
          <div style={{ padding: '20px' }}>
            <QueryForm selectedQuery={activeQuery} setSelectedQuery={(setActiveQuery)} />
          </div>
        </Column>
      </Row>
    </Page>
  );
};

const mapStateToProps = ({ entities }: RootState) => ({
  queries: entities?.query.queries,
  queryResult: entities?.query.result,
  isRunning: entities?.query.isRunning,
});

const mapDispatchToProps = {
  getQueries,
  deleteQuery,
  upsertQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(Query);

export { Container as Query };
